<script lang="ts">
	import { Models as M } from "@lux-ai/api-interface"
	import { getProgressController } from "../../ProgressHandler.svelte"
	import { LoadingText, ProgressBar, ShowcaseTile } from "misc-ui"
	import { LuxAIController } from "../../LuxAIController.svelte"
	import { closeOverlay, openConfirmOverlay } from "overlay"
	import { Responsive } from "responsive"
	import { cancelGeneration } from "../../../modules/operations"

	interface Props {
		theme: M.Theme
		controller: LuxAIController
		activeGeneration?: M.ThemeGeneration
	}

	let { theme, controller, activeGeneration }: Props = $props()

	const user = controller.userDataGetter.getCurrentUserData()
	const isUserSubscribed = user.isSubscribed || user.isAISuperUser

	let tokenCost = theme.free ? 0 : 10
	let scene = controller.themedScenes.find((s) => s.theme_id === theme.id)

	const release = $derived(new Date(theme.releaseDate))
	const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
	const isNew = $derived(release.getTime() > oneWeekAgo)
	let isPrivate = $derived(!theme.public)
	let isCancelling = $state(false)

	let userCanAfford = user.availableTokens >= tokenCost || user.isAISuperUser

	let onSelect = controller.selectTheme

	let onViewGeneration = () => {
		activeGeneration && controller.viewGeneration(activeGeneration)
	}

	let onViewExistingScene = () => {
		scene && controller.quickActions.viewScene(Number(scene.id))
	}

	let onViewGeneratedScene = async () => {
		if (!activeGeneration.scene_id) return

		controller.refreshThemedScenes()
		await controller.refreshActiveGenerations()

		controller.quickActions.viewScene(activeGeneration.scene_id)
	}

	let progressController = getProgressController()
	let generationProgress = $derived(progressController.progress[activeGeneration?.id])
	let activeGenerationAmount = $derived(controller.activeGenerations.length)
	let isGenerating = $derived(generationProgress < 1)
	let isGenerationAllowed = $derived(userCanAfford || (theme.free && activeGenerationAmount == 0))

	function verifyCancel() {
		const overlayId = openConfirmOverlay({
			buttons: {
				confirm: {
					text: "Yes, cancel",
					onClick: () => {
						isCancelling = true
						cancelGeneration(activeGeneration.id, controller)
					},
				},
				deny: {
					text: "No",
					onClick: () => closeOverlay(overlayId),
				},
			},
			prompt: [`Are you sure you want to cancel generating ${theme.name}? `],
		})
	}
</script>

<div class="theme-instance" id="theme-instance-{theme.id}" class:isMobile={Responsive.isMobile}>
	<ShowcaseTile
		title={theme.name}
		description={theme.description}
		imagePreviewSrc={theme.thumbnailSrc}
		videoPreviewSrc={theme.videoSrc}
	>
		{#snippet heading()}
			<h2 class="theme-heading">
				<span class="heading-text">
					{theme.name}
				</span>
				{#if theme.free}
					<span class="free-tag"> Free </span>
					<!-- {:else}
					<span class="free-tag">
						{tokenCost} tokens 
						</span>
						-->
				{/if}
				{#if isNew}
					<span class="free-tag alt"> New </span>
				{/if}
				{#if isPrivate}
					<span class="free-tag alt"> Unreleased </span>
				{/if}
			</h2>
		{/snippet}
		{#snippet children()}
			{#if activeGeneration}
				{#if isGenerating}
					<div class="generation-indicator"><LoadingText content={["Generating"]} /></div>
				{:else}
					<div class="generation-indicator">Generation complete</div>
				{/if}
				<div class="progress-bar-container">
					<ProgressBar percentage={generationProgress} width="100%" />
				</div>
			{/if}
		{/snippet}
		{#snippet bottomRow()}
			<div class="actions">
				{#if activeGeneration}
					{#if activeGeneration.scene_id}
						<button class="outline-button square-button" onclick={onViewGeneratedScene}>View</button
						>
					{:else}
						<button class="outline-button square-button alt" onclick={() => onViewGeneration()}
							>View</button
						>
					{/if}
					{#if isGenerating}
						<button
							class="outline-button square-button warn"
							onclick={() => verifyCancel()}
							disabled={isCancelling}
						>
							Cancel
						</button>
					{/if}
				{:else}
					{#if isUserSubscribed || theme.free}
						<button
							class="outline-button square-button"
							onclick={() => onSelect(theme)}
							disabled={!isGenerationAllowed}>Generate</button
						>
					{:else}
						<button
							class="outline-button square-button alt"
							onclick={controller.quickActions.jumpToSubscribe}>Subscribe Now</button
						>
					{/if}
					{#if scene}
						<button class="outline-button square-button alt" onclick={onViewExistingScene}
							>View Scene</button
						>
					{/if}
				{/if}
			</div>
		{/snippet}
	</ShowcaseTile>
</div>

<style>
	.generation-indicator {
		position: absolute;
		top: 0;
		bottom: 0;
		right: 0;
		left: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100%;
		background-color: var(--color-bg);
		z-index: 2;
	}
	.theme-instance {
		position: relative;
	}

	.isMobile.theme-instance {
		width: 100%;
	}

	.theme-heading {
		font-size: var(--h2);
		margin: 0;
		display: flex;
		flex-direction: row;
		align-items: center;
		text-wrap: nowrap;
		overflow: hidden;
		white-space: nowrap;
	}

	.theme-heading .heading-text {
		text-overflow: ellipsis;
		white-space: nowrap;
		flex: 1 1 auto;
		min-width: 4rem;
		overflow: hidden;
	}

	.free-tag {
		flex-shrink: 0;
		padding: 0.1rem 0.5rem;
		margin-left: 0.25rem;
		color: var(--color-main);
		border-radius: 0.25rem;
		box-shadow: var(--shadow-small);
		background-color: var(--color-bg);
	}

	.free-tag.alt {
		color: var(--color-main);
		background-color: var(--color-bg);
	}

	.generation-indicator,
	.generation-indicator :global(.loading-text) {
		font-size: var(--h1);
	}

	.progress-bar-container {
		position: absolute;
		bottom: -0.1rem;
		left: -0.5rem;
		width: calc(100% + 1rem);
		z-index: 2;
	}

	.progress-bar-container :global(.progress-bar) {
		margin: 0;
	}

	.actions {
		margin-top: 0.5rem;
	}

	.actions button {
		padding: 0rem 1rem;
	}
</style>
